import { createFileRoute, Link } from "@tanstack/react-router";
import { Lightbulb, BookOpen, ShieldCheck, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SiteHeader } from "@/components/SiteHeader";
const FAQS = [
  {
    q: "Can you help me write an essay?",
    a: "Yes. SmartEssay gives you essay help by building a framed outline around your title or prompt. You'll get sentence starters, real cited quotes, and a logical structure so you can fill in your own analysis and voice.",
  },
  {
    q: "Can you help me with my essay if I'm already started?",
    a: "Yes — paste your draft into the Essay Reviewer and we'll show you evidence gaps with real cited quotes, a paragraph-by-paragraph flow map, and prioritized improvements you can act on.",
  },
  {
    q: "Is this essay help service free?",
    a: "You can try the framed outline and Essay Reviewer free. Paid plans unlock unlimited use, MLA and APA 7 PDF exports, and saved history.",
  },
  {
    q: "How is this different from a generic AI chatbot?",
    a: "Chatbots write essays for you; SmartEssay scaffolds the writing so you still learn the process. Quotes are real, citations are formatted correctly, and the outline matches your voice instead of replacing it.",
  },
  {
    q: "What citation styles does it support?",
    a: "MLA and APA 7. When you export, APA references are italicized automatically and formatting follows the current style guide.",
  },
];

export const Route = createFileRoute("/essay-help")({
  head: () => ({
    meta: [
      { title: "Essay Help for Students — Framed Outlines & Cited Quotes" },
      { name: "description", content: "Free essay help for students: framed outlines, real cited quotes, MLA and APA 7 exports, and a draft reviewer that finds evidence gaps and flow issues." },
      { property: "og:title", content: "Essay Help for Students — SmartEssay" },
      { property: "og:description", content: "Framed outlines, real cited quotes, and a draft reviewer that finds evidence gaps and flow issues." },
      { property: "og:url", content: "https://smartessay.ai/essay-help" },
    ],
    links: [{ rel: "canonical", href: "https://smartessay.ai/essay-help" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    }],
  }),
  component: EssayHelpPage,
});

function EssayHelpPage() {
  return (
    <div className="min-h-screen bg-hero-gradient">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-6 pb-24">
        <header className="text-center">
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">Essay help</p>
          <h1 className="mt-3 font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
            <em>Essay help</em> that teaches you the structure
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Stuck on an essay? SmartEssay gives you essay help built by a 27-year English teacher: a framed outline with real cited quotes, sentence starters, and a draft reviewer that catches evidence gaps and flow issues. You learn the process, not just the product.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="shadow-glow">
              <Link to="/generator">Get essay help now</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/reviewer" search={{ try: "1" }}>Review my draft</Link>
            </Button>
          </div>
        </header>

        <section className="mt-14 grid gap-6 sm:grid-cols-3">
          {[
            { icon: Lightbulb, title: "Start from a frame", body: "A teacher-built scaffold turns a blank page into a clear path forward." },
            { icon: BookOpen, title: "Real cited evidence", body: "Word-for-word quotes with MLA or APA 7 citations done for you." },
            { icon: ListChecks, title: "Catch what you missed", body: "The reviewer flags weak claims, missing evidence, and broken transitions." },
          ].map((f) => (
            <div key={f.title} className="rounded-2xl border border-border bg-card p-5 shadow-soft">
              <f.icon className="h-5 w-5 text-primary" />
              <h2 className="mt-3 font-display text-base font-semibold">{f.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
            </div>
          ))}
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-medium">Essay help — common questions</h2>
          <Accordion type="single" collapsible className="mt-4">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`}>
                <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section className="mt-14 rounded-2xl border border-border bg-card p-8 text-center shadow-soft">
          <ShieldCheck className="mx-auto h-6 w-6 text-primary" />
          <h2 className="mt-3 font-display text-xl font-semibold">Start your essay in under a minute</h2>
          <p className="mt-2 text-sm text-muted-foreground">Drop in a title and get a framed outline with real cited quotes.</p>
          <Button asChild className="mt-5 shadow-glow">
            <Link to="/generator">Get started</Link>
          </Button>
        </section>
      </main>
    </div>
  );
}
